import { useState, useEffect } from "react";
import { PageHeader } from "../../../components";
import { Card } from "../../../components/card.component";
import { SelectCompany } from "../../../features/data-exchange/selectCompany.component";
import {
  ActivateSync,
  AppointmentDataExchange,
  CaregiverDataExchangeTable,
  PatientDataExchangeTable,
} from "../../../features/data-exchange";
import { StorageAuth } from "../../../core/localStorage";
import { ApiDataExchange } from "../../../core/apis/dataExchange";
import { showErrorToast } from "../../../components/toast.component";

export function PageSuperAdminDataExchange() {
  const [selectedCompany, setSelectedCompany] = useState<string>("");
  const [hasToken, setHasToken] = useState(false);
  const [activeTab, setActiveTab] = useState("appointment");
  const [isRefreshingToken, setIsRefreshingToken] = useState(false);

  // Initialize with existing company from StorageAuth if available
  useEffect(() => {
    const storedCompanyId = StorageAuth.CompanyId;
    if (storedCompanyId) {
      setSelectedCompany(storedCompanyId);
    }
  }, []);

  // Update StorageAuth when selectedCompany changes
  useEffect(() => {
    if (selectedCompany) {
      StorageAuth.CompanyId = selectedCompany;

      // Reset token state and request a new token
      setHasToken(false);
      refreshTokenForCompany(selectedCompany);
    }
  }, [selectedCompany]);

  // Function to get a new token when company changes
  const refreshTokenForCompany = async (companyId: string) => {
    if (!companyId) return;

    setIsRefreshingToken(true);
    await ApiDataExchange.GetNewToken({
      companyId,
      onStart: () => {},
      onEnd: () => setIsRefreshingToken(false),
      onSuccess: () => setHasToken(true),
      onFail: () => {
        setHasToken(false);
        showErrorToast({
          isSuccess: false,
          message: "Failed to get token for the selected company",
        });
      },
    });
  };

  return (
    <Card
      header={
        <div className="d-flex justify-content-between align-items-center w-100">
          <PageHeader title="Data Exchange" />
          <div className="col-md-2 col-sm-4">
            <SelectCompany setSelectedCompany={setSelectedCompany} />
          </div>
        </div>
      }
    >
      <div className="p-3">
        {!selectedCompany && (
          <div className="alert alert-info text-center py-4 my-3" role="alert">
            <i className="bi bi-info-circle me-2"></i>
            <span className="fw-semibold">
              Please select a company to view data exchange information.
            </span>
          </div>
        )}

        {selectedCompany && (
          <div>
            <div className="d-flex justify-content-end my-2">
              <ActivateSync setHasToken={setHasToken} />
            </div>

            {hasToken && selectedCompany && (
              <>
                <div>
                  <ul className="nav nav-tabs mb-3" role="tablist">
                    <li className="nav-item" role="presentation">
                      <button
                        className={`nav-link ${
                          activeTab === "appointment" ? "active" : ""
                        }`}
                        id="appointment-tab"
                        data-bs-toggle="tab"
                        data-bs-target="#appointment"
                        type="button"
                        role="tab"
                        onClick={() => setActiveTab("appointment")}
                      >
                        Appointment
                      </button>
                    </li>
                    <li className="nav-item" role="presentation">
                      <button
                        className={`nav-link ${
                          activeTab === "caregiver" ? "active" : ""
                        }`}
                        id="caregiver-tab"
                        data-bs-toggle="tab"
                        data-bs-target="#caregiver"
                        type="button"
                        role="tab"
                        onClick={() => setActiveTab("caregiver")}
                      >
                        Caregiver
                      </button>
                    </li>
                    <li className="nav-item" role="presentation">
                      <button
                        className={`nav-link ${
                          activeTab === "patient" ? "active" : ""
                        }`}
                        id="patient-tab"
                        data-bs-toggle="tab"
                        data-bs-target="#patient"
                        type="button"
                        role="tab"
                        onClick={() => setActiveTab("patient")}
                      >
                        Patient
                      </button>
                    </li>
                  </ul>

                  <div className="tab-content">
                    <div
                      className={`tab-pane fade ${
                        activeTab === "appointment" ? "show active" : ""
                      }`}
                      id="appointment"
                      role="tabpanel"
                    >
                      <AppointmentDataExchange
                        selectedCompany={selectedCompany}
                        key={selectedCompany}
                      />
                    </div>
                    <div
                      className={`tab-pane fade ${
                        activeTab === "patient" ? "show active" : ""
                      }`}
                      id="patient"
                      role="tabpanel"
                    >
                      <PatientDataExchangeTable
                        selectedCompany={selectedCompany}
                        key={selectedCompany}
                      />
                    </div>
                    <div
                      className={`tab-pane fade ${
                        activeTab === "caregiver" ? "show active" : ""
                      }`}
                      id="caregiver"
                      role="tabpanel"
                    >
                      <CaregiverDataExchangeTable
                        selectedCompany={selectedCompany}
                        key={selectedCompany}
                      />
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </Card>
  );
}
